import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Button,
  Badge as MuiBadge,
} from "@mui/material";
import { Search as SearchIcon, KeyboardArrowDown } from "@mui/icons-material";
import { CheckCircle } from "lucide-react";
import Spinner from "../../../components/Spinner.jsx";
import TabList from "../../../components/TabList.jsx";
import { borderRadius, colorsFoundation } from "../../../theme/index.js";
import typography from "../../../theme/tokens/typography.js";
import { ListContainer, ListItem } from "./lists.jsx";
import { getDatumZeit, getEuro } from "../../../services/formatService.js";

const PaymentMatchingClaimList = ({
  statusFilter,
  onStatusFilterChange,
  statusOptions,
  claimsSearchword,
  onClaimsSearchwordChange,
  matchFilter,
  onMatchFilterChange,
  claimsListRef,
  onClaimsScroll,
  loadingClaims,
  sortedFilteredClaims,
  matchPayments,
  selectedClaim,
  onSelectClaim,
  loadingMoreClaims,
  hasMoreClaims,
  approveAllExactMatches,
  isAssigning,
}) => {
  const renderClaimContent = (claim) => {
    const matches = matchPayments(claim);

    return (
      <ListItem
        key={`claim_${claim.id}`}
        selected={selectedClaim?.id === claim.id}
        onClick={() => onSelectClaim(claim)}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {claim.schaden_nummer_makler}
            </Typography>
            {!!claim.schaden_nummer_vu && (
              <Typography variant="body2">
                VU: {claim.schaden_nummer_vu}
              </Typography>
            )}
            <Typography variant="body2">
              VN: {claim?.vertrag?.vertragsnummer}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {claim?.vertrag?.gesellschaft?.name}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Datum:
                <br />
                {claim.schaden_datum ? getDatumZeit(claim.schaden_datum) : "-"}
              </Typography>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">
                  {claim.betrag_label}:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                  }}
                >
                  {getEuro(claim.betrag)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {matches.total ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                alignItems: "flex-end",
              }}
            >
              {matches.exact.length > 0 && (
                <MuiBadge
                  badgeContent={matches.exact.length}
                  color="primary"
                  overlap="circular"
                >
                  <Box sx={{ width: 24, height: 24 }} />
                </MuiBadge>
              )}
              {matches.possible.length > 0 && (
                <MuiBadge
                  badgeContent={matches.possible.length}
                  color="warning"
                  overlap="circular"
                >
                  <Box sx={{ width: 24, height: 24 }} />
                </MuiBadge>
              )}
            </Box>
          ) : null}
        </Box>
      </ListItem>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: borderRadius.md,
        border: `1px solid ${colorsFoundation.neutral[200]}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontSize: typography.size.xl }}>
          Schäden
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: typography.size.sm, whiteSpace: "nowrap" }}
          >
            Status
          </Typography>
          <TextField
            id="payment-matching-status"
            select
            variant="standard"
            size="small"
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: typography.size.sm,
                paddingY: 0,
                paddingRight: 1,
                width: "auto",
              },
              "& .MuiInputBase-input": {
                paddingTop: 0.25,
                paddingBottom: 0.25,
              },
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:after": { borderBottom: "none" },
              "&:hover .MuiInput-underline:before": {
                borderBottom: "none !important",
              },
            }}
            slotProps={{
              select: {
                IconComponent: (props) => <KeyboardArrowDown {...props} />,
              },
            }}
          >
            <MenuItem key="all" value="all">
              Alle
            </MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          id="payment-matching-claim-search"
          placeholder="Schäden suchen..."
          value={claimsSearchword}
          onChange={(e) => onClaimsSearchwordChange(e.target.value)}
          fullWidth
          size="small"
          label="Schaden suchen"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Zuordnungsfilter
        </Typography>
        <Box sx={{ mt: 1 }}>
          <TabList
            tabs={[
              { key: "all", label: "Alle" },
              { key: "exact", label: "Exakt" },
              { key: "possible", label: "Möglich" },
              { key: "none", label: "Keine" },
            ]}
            activeTab={matchFilter}
            onTabChange={onMatchFilterChange}
            containerSx={{
              mt: 0,
              borderBottom: `1px solid ${colorsFoundation.neutral[200]}`,
            }}
          />
        </Box>
      </Box>
      <Box
        ref={claimsListRef}
        onScroll={onClaimsScroll}
        sx={{ flex: 1, overflow: "auto", mt: 1 }}
      >
        {loadingClaims ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Spinner size={24} />
          </Box>
        ) : (
          <ListContainer>
            {sortedFilteredClaims.map((claim) => renderClaimContent(claim))}

            {!loadingMoreClaims && sortedFilteredClaims.length === 0 && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Keine Schäden gefunden
                </Typography>
              </Box>
            )}

            {loadingMoreClaims && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                <Spinner size={20} />
              </Box>
            )}

            {!hasMoreClaims && sortedFilteredClaims.length > 0 && (
              <Box sx={{ py: 2, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  Alle Schäden geladen
                </Typography>
              </Box>
            )}
          </ListContainer>
        )}
      </Box>
      {matchFilter === "exact" && sortedFilteredClaims.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sie können alle Zahlungen, die exakt den Schäden zugeordnet wurden,
            hier gesammelt bestätigen.
          </Typography>
          <Button
            variant="contained"
            color="success"
            size="medium"
            fullWidth
            onClick={approveAllExactMatches}
            disabled={isAssigning}
            startIcon={<CheckCircle size={16} />}
          >
            {isAssigning ? "Wird zugeordnet..." : "Exakte Zahlungen zuordnen"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

PaymentMatchingClaimList.propTypes = {
  statusFilter: PropTypes.string.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  claimsSearchword: PropTypes.string.isRequired,
  onClaimsSearchwordChange: PropTypes.func.isRequired,
  matchFilter: PropTypes.string.isRequired,
  onMatchFilterChange: PropTypes.func.isRequired,
  claimsListRef: PropTypes.shape({ current: PropTypes.any }),
  onClaimsScroll: PropTypes.func.isRequired,
  loadingClaims: PropTypes.bool.isRequired,
  sortedFilteredClaims: PropTypes.arrayOf(PropTypes.object).isRequired,
  matchPayments: PropTypes.func.isRequired,
  selectedClaim: PropTypes.object,
  onSelectClaim: PropTypes.func.isRequired,
  loadingMoreClaims: PropTypes.bool.isRequired,
  hasMoreClaims: PropTypes.bool.isRequired,
  approveAllExactMatches: PropTypes.func.isRequired,
  isAssigning: PropTypes.bool.isRequired,
};

export default PaymentMatchingClaimList;
